import { AutoCompleteSection } from '../../../shared/components/CustomTextField';
import { pduPruToReadableName } from '../../../types/item.mapper';
import { useCallback } from 'react';
import { useDateFormatting } from '../../../shared/hooks/useDateFormatting';

export type ProgressState = 'Not started' | 'Ongoing' | 'Completed';
export type ImportStatus = 'New' | 'Updated';

interface AutoCompleteProps {
   dropDownData: any;
   materialParts: string[];
   partDetails: string[];
   usedPartDetails?: string[];
   itemStates: string[];
   parts?: string[];
   currentPart?: string | null;
   currentPartDetail?: string | null;
   isBulk?: boolean;
   getPartDetailOptions: () => string[];
   getFilteredParts: (partDetail: string | undefined, materialParts: string[]) => string[];
   getCompatiblePartDetails: (part: string) => string[];
   getMaterialGradeOptions: (partDetail?: string, part?: string) => string[];
   getPartOptions: () => string[];
}

export function useAutoCompleteSections({
   dropDownData,
   materialParts,
   usedPartDetails = [],
   itemStates,
   parts = [],
   currentPart,
   currentPartDetail,
   isBulk = false,
   getPartDetailOptions,
   getFilteredParts,
   getCompatiblePartDetails,
   getMaterialGradeOptions,
   getPartOptions,
}: AutoCompleteProps) {
   const { cleanDate, sortDates } = useDateFormatting();
   const usedInSectionName = isBulk ? 'Used in selected items' : 'Used in current item';

   const getAutoCompleteSections = useCallback(
      (accessor: string): AutoCompleteSection[] => {
         const sections: AutoCompleteSection[] = [];

         if (accessor === 'part') {
            const filteredParts = parts.filter(Boolean);

            if (currentPartDetail) {
               const compatibleParts = getFilteredParts(currentPartDetail, materialParts) || [];
               if (compatibleParts.length > 0) {
                  const filteredCompatibleParts = compatibleParts.filter((p) => !parts.includes(p));
                  if (filteredCompatibleParts.length > 0) {
                     sections.push({
                        name: `Compatible with ${currentPartDetail}`,
                        values: filteredCompatibleParts,
                     });
                  }
               }

               if (filteredParts.length > 0) {
                  sections.push({
                     name: usedInSectionName,
                     values: filteredParts,
                  });
               }
            } else {
               if (filteredParts.length > 0) {
                  sections.push({
                     name: usedInSectionName,
                     values: filteredParts,
                  });
               }
            }

            const allParts = getPartOptions();
            const usedParts = new Set(sections.flatMap((section) => section.values));
            const remainingParts = allParts.filter((p) => !usedParts.has(p));
            if (remainingParts.length > 0) {
               sections.push({
                  name: 'All parts',
                  values: remainingParts,
               });
            }
         } else if (accessor === 'partDetail') {
            if (currentPart) {
               const compatibleDetails = getCompatiblePartDetails(currentPart);
               if (compatibleDetails.length > 0) {
                  sections.push({
                     name: `Compatible with ${currentPart}`,
                     values: compatibleDetails,
                  });
               }

               if (usedPartDetails.length > 0) {
                  sections.push({
                     name: usedInSectionName,
                     values: usedPartDetails,
                  });
               }
            } else {
               if (usedPartDetails.length > 0) {
                  sections.push({
                     name: usedInSectionName,
                     values: usedPartDetails,
                  });
               }
            }

            const allOptions = getPartDetailOptions();
            const usedOptions = new Set(sections.flatMap((section) => section.values));
            const remainingOptions = allOptions.filter((o) => !usedOptions.has(o));
            if (remainingOptions.length > 0) {
               sections.push({
                  name: 'All part details',
                  values: remainingOptions,
               });
            }
         } else if (accessor === 'materialGrade') {
            if (currentPart || currentPartDetail) {
               const compatibleGrades = getMaterialGradeOptions(currentPartDetail ?? undefined, currentPart ?? undefined);
               if (compatibleGrades.length > 0) {
                  sections.push({
                     name:
                        currentPart && currentPartDetail
                           ? `Compatible with ${currentPartDetail} ${currentPart}`
                           : currentPart
                           ? `Compatible with ${currentPart}`
                           : 'Compatible material grades',
                     values: compatibleGrades,
                  });
               }
            }
         }

         return sections;
      },
      [
         parts,
         usedPartDetails,
         currentPart,
         currentPartDetail,
         materialParts,
         usedInSectionName,
         getPartDetailOptions,
         getFilteredParts,
         getCompatiblePartDetails,
         getMaterialGradeOptions,
         getPartOptions,
      ]
   );

   const getAutoCompleteOptions = useCallback(
      (key: string): string[] => {
         switch (key) {
            case 'buCodePru':
            case 'buCodePdu': {
               const codes = dropDownData[`${key}s`] || [];
               return codes.map(pduPruToReadableName);
            }
            case 'ssd':
               return dropDownData.saleStartDatesGlobal ? cleanDate(dropDownData.saleStartDatesGlobal) : [];
            case 'eds':
               return dropDownData.saleEndDatesGlobal ? cleanDate(sortDates(dropDownData.saleEndDatesGlobal)).concat(['Not set']) : [];
            case 'hfbs':
               return dropDownData.hfbs || [];
            case 'pas':
               return dropDownData.pas || [];
            case 'familyName':
               return dropDownData.familyNames || [];
            case 'partDetail':
               return getPartDetailOptions();
            case 'part':
               return getPartOptions();
            case 'materialGrade': {
               const sections = getAutoCompleteSections('materialGrade');
               return sections.flatMap((section) => section.values);
            }
            case 'itemState':
               return itemStates;
            case 'progressState':
               return ['Not started', 'Ongoing', 'Completed'];
            case 'piaImportStatus':
               return ['New', 'Updated'];
            default:
               return [];
         }
      },
      [dropDownData, cleanDate, sortDates, getPartDetailOptions, getPartOptions, itemStates, getAutoCompleteSections]
   );

   return {
      getAutoCompleteSections,
      getAutoCompleteOptions,
   };
}
